import React, { useContext, useMemo } from "react";
import useWindowDimensions from "../../../Hooks/useWindowDimensions";
import Location from "../../../Assets/Images/location-red.png";
import Location2 from "../../../Assets/Images/location-yellow.png";
import Location3 from "../../../Assets/Images/location-same.png";
import Location4 from "../../../Assets/Images/location-blue-point.png";
import { translate } from "../../translate/translate";
import LanguageContext from "../../../Store/LanguageContext";
import MultipleAssessmentsContext from "../../../Store/MultipleAssessmentsContext";

function MilestoneBarMultipleAssessments(props) {
  const { 
    location = -1, 
    location2 = -1, 
    statusTags = ["MW1", "MW3"], 
    years = ["2023-2024", "2024-2025"],
    from = ""
  } = props;

  const { windowWidth } = useWindowDimensions();
  const langCtx = useContext(LanguageContext);
  const multipleAssessmentsCtx = useContext(MultipleAssessmentsContext);
  
  const activeStatus = useMemo(() => {
    const deployments = multipleAssessmentsCtx?.deployments || [];
    const parishInsights = multipleAssessmentsCtx?.parishInsights || [];
    
    if (deployments.length === 0 || parishInsights.length < 2) {
      return { isLocationActive: false, isLocation2Active: false };
    }
    const activeDeploymentId = deployments[deployments.length - 1]?.deploymentId;
    if (!activeDeploymentId) {
      return { isLocationActive: false, isLocation2Active: false };
    }
    
    const locationDeploymentId = parishInsights[0]?.deploymentId;
    const location2DeploymentId = parishInsights[1]?.deploymentId;
    
    return {
      isLocationActive: locationDeploymentId === activeDeploymentId,
      isLocation2Active: location2DeploymentId === activeDeploymentId
    };
  }, [multipleAssessmentsCtx?.deployments, multipleAssessmentsCtx?.parishInsights]);
  
  const { isLocationActive, isLocation2Active } = activeStatus;

  const handleTranslate = (text, type) => {
    switch (langCtx.language) {
      case "Nederlands": {
        return translate(text, "milestoneBarDutch", type)
      }
      default: {
        return text;
      }
    }
  }

  const renderWithBreak = (text, type, breakAfterWords) => {
    const translated = handleTranslate(text, type)
    const words = (translated || '').split(' ')
    
    if (windowWidth > 540) {
      if (translated === "Het hijsen van de zeilen") {
        return <>Het hijsen<br/> van de zeilen</>
      }

      if (translated === "De golven bedwingen") {
        return <>De golven<br/> bedwingen</>
      }
    }
    
    if (words.length <= breakAfterWords) return translated
    const first = words.slice(0, breakAfterWords).join(' ')
    const second = words.slice(breakAfterWords).join(' ')
    return <>{first}<br/> {second}</>
  }

  const locationImages = useMemo(() => {
    const images = {};
    
    for (let i = 0; i <= 5; i++) {
      if (location === location2 && location === i) {
        if (isLocationActive || isLocation2Active) {
          images[i] = Location3;
        } else {
          images[i] = Location4;
        }
      } 
      else if (i === location) {
        images[i] = isLocationActive ? Location2 : Location;
      } 
      else if (i === location2) {
        images[i] = isLocation2Active ? Location2 : Location;
      } 
      else {
        images[i] = Location;
      }
    }
    
    return images;
  }, [location, location2, isLocationActive, isLocation2Active]);

  const getLocationImage = (milestoneIndex) => {
    return locationImages[milestoneIndex] || Location;
  }

  const milestoneData = useMemo(() => {
    const data = {};
    const isBothAtSameLocation = location === location2;
    
    for (let i = 0; i <= 5; i++) {
      if (isBothAtSameLocation && location === i) {
        const location2TagClass = isLocation2Active ? "yellow" : "red";
        data[i] = {
          statusTags: (
            <div style={{ display: 'flex', gap: '8px', position: 'absolute', top: '-71px', left: '-36px' }}>
              <span className="status-tag red" style={{ position: 'relative', top: 'auto', left: 'auto' }}>{statusTags[0]}</span>
              <span className={`status-tag ${location2TagClass}`} style={{ position: 'relative', top: 'auto', left: 'auto' }}>{statusTags[1]}</span>
            </div>
          ),
          year: years[1]
        };
      } else if (location === i) {
        data[i] = {
          statusTags: <span className="status-tag red">{statusTags[0]}</span>,
          year: years[0]
        };
      } else if (location2 === i) {
        const location2TagClass = isLocation2Active ? "yellow" : "red";
        data[i] = {
          statusTags: <span className={`status-tag ${location2TagClass}`}>{statusTags[1]}</span>,
          year: years[1]
        };
      } else {
        data[i] = {
          statusTags: null,
          year: null
        };
      }
    }
    return data;
  }, [location, location2, statusTags, years, isLocation2Active]);

  const getStatusTagAndYear = (milestoneIndex) => {
    return milestoneData[milestoneIndex] || { statusTags: null, year: null };
  }

  const milestones = useMemo(() => [
    { index: 0, className: "ma-milestone-lookers", textKey: "Onlookers", translationKey: "onlookers" },
    { index: 1, className: "ma-milestone-on-shore", textKey: "On the Shore", translationKey: "on-the-shore" },
    { index: 2, className: "ma-milestone-casting-off", textKey: "Casting Off", translationKey: "casting-off" },
    { index: 3, className: "ma-milestone-raising-sails", textKey: "Raising the Sails", translationKey: "raising-the-sails", useRenderBreak: true },
    { index: 4, className: "ma-milestone-sailing-waves", textKey: "Sailing the Waves", translationKey: "sailing-the-waves", useRenderBreak: true },
    { index: 5, className: "ma-milestone-beacon-parish", textKey: "Beacon Parish", translationKey: "beacon-parish", useRenderBreak: true }
  ], []);

  return (
    <div className="ma-milestone-cover">
      <div className="ma-milestone-bar">
        {milestones.map((milestone) => {
          const isActive = location === milestone.index || location2 === milestone.index;
          const statusData = milestoneData[milestone.index];
          
          return (
            <div key={milestone.index} className={`${milestone.className} ${isActive ? "ma-milestone-active" : ""}`}>
              <div className="ma-milestone-circle" />
              {statusData.statusTags}
              <p>
                {milestone.useRenderBreak 
                  ? renderWithBreak(milestone.textKey, milestone.translationKey, 1)
                  : handleTranslate(milestone.textKey, milestone.translationKey)
                }
              </p>
              {statusData.year && <p className="ma-milestone-year">{statusData.year}</p>}
              <div className="ma-milestone-location">
                <img className="ma-milestone-location-img" src={getLocationImage(milestone.index)} alt="" />
              </div>
            </div>
          );
        })}
        <div className="triangle triangle-three"/>
      </div>
    </div>
  );
}

export default MilestoneBarMultipleAssessments;
